import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Builder } from '@builder.io/react';
import noop from 'lodash-es/noop';

import {
  ColorClassMap,
  COLOR_NAMES,
  COLOR_NAME_WHITE,
} from '@/shared/constants/colors';
import { cleanBuilderStringInput } from '@/shared/text';

import { CarouselIndicator } from './CarouselIndicator';

interface BuilderInput {
  title: string;
  titleColor: string;
  subTitle: string;
  subTitleColor: string;
  exclusiveItems: { backgroundImage: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: any;
}

type Props = { locale: string } & BuilderInput;

export const ExclusiveSelectionHeader = ({
  title = 'Title',
  titleColor = ColorClassMap[COLOR_NAME_WHITE],
  subTitle = 'SubTitle',
  subTitleColor = ColorClassMap[COLOR_NAME_WHITE],
  exclusiveItems,
  attributes,
}: Props) => {
  const [currentItem, setCurrentItem] = useState<number>(-1);
  const [animated, setAnimated] = useState<boolean>(false);
  const defaultBackgroudItems =
    (exclusiveItems && exclusiveItems.map((item) => item.backgroundImage)) ||
    [];

  const [backgroundItems, setBackgroundItems] = useState<string[]>(
    defaultBackgroudItems
  );

  useEffect(() => {
    if (!exclusiveItems) return noop;
    if (exclusiveItems.length < 2) return noop;

    const timeout = setTimeout(() => {
      const nextItem = currentItem + 1;
      if (nextItem > exclusiveItems.length - 1) {
        setCurrentItem(0);
      } else {
        setCurrentItem(nextItem);
      }

      if (currentItem === -1) return;
      setAnimated(true);
      setTimeout(() => {
        setBackgroundItems([...backgroundItems.slice(1), backgroundItems[0]]);
        setAnimated(false);
      }, 1500);
    }, 3500);
    return () => clearTimeout(timeout);
  }, [exclusiveItems, currentItem, backgroundItems]);

  return (
    <div {...attributes}>
      <div className="p-es-header">
        <div
          className={classNames('p-es-images-container', {
            'p-es-animated': animated,
          })}
        >
          {backgroundItems.map((item, index) => {
            const key = `${item}-${index}`;
            return (
              <div className="p-es-image p-es-image-scroll" key={key}>
                <img alt="item" src={item} />
              </div>
            );
          })}
        </div>
        <div
          className={classNames(
            'u-wrapper',
            'p-es-wrapper',
            'u-m-t-lg-l',
            'u-p-t-lg-xxl-14',
            'u-p-b-lg-xxl-14',
            'u-m-t-md-l',
            'u-p-t-md-xxl-14',
            'u-p-b-md-xxl-14'
          )}
        >
          <div className="p-es-header-title">
            <h1
              className={classNames(
                'u-typography-h1-serif',
                'u-m-b-l',
                titleColor
              )}
            >
              {cleanBuilderStringInput(title, true)}
            </h1>
            <h4
              className={classNames(
                'u-typography-h4',
                'tw:hidden tw:md:block',
                subTitleColor
              )}
            >
              {cleanBuilderStringInput(subTitle, true)}
            </h4>
          </div>
        </div>
        {exclusiveItems && exclusiveItems.length > 1 && (
          <div className="p-es-indicator tw:hidden tw:md:flex">
            <CarouselIndicator
              count={exclusiveItems.length}
              activeIndex={currentItem}
            />
          </div>
        )}
      </div>
      <div className="p-es-mobile tw:md:hidden u-p-h-m u-p-v-l tw:bg-neutral-tertiary">
        <h4 className="u-typography-h4 u-color-black">
          {cleanBuilderStringInput(subTitle, true)}
        </h4>
      </div>
    </div>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export function componentRegister() {
  const name = 'ExclusiveSelectionHeader';
  Builder.registerComponent(ExclusiveSelectionHeader, {
    name,
    inputs: [
      {
        name: 'title',
        type: 'string',
      },
      {
        name: 'titleColor',
        friendlyName: 'Title Color',
        type: 'string',
        defaultValue: ColorClassMap[COLOR_NAME_WHITE],
        enum: COLOR_NAMES.map((value) => ({
          label: value,
          value: ColorClassMap[value],
        })),
      },
      {
        name: 'subTitle',
        type: 'string',
      },
      {
        name: 'subTitleColor',
        friendlyName: 'Sub Title Color',
        type: 'string',
        defaultValue: ColorClassMap[COLOR_NAME_WHITE],
        enum: COLOR_NAMES.map((value) => ({
          label: value,
          value: ColorClassMap[value],
        })),
      },
      {
        name: 'exclusiveItems',
        type: 'list',
        subFields: [
          {
            name: 'backgroundImage',
            type: 'file',
            allowedFileTypes: ['jpeg', 'png'],
          },
        ],
      },
    ],
    hideFromInsertMenu: true,
    noWrap: true,
  });
  return name;
}
